import { useAppSelector, useAppDispatch } from "@/services/hooks";
import { useCurrentPermission } from "@/services/userServices/useCurrentPermission";
import React, { useCallback, useEffect, useMemo, useState } from "react";

import {
  addProperty,
  clearMessage,
  restoreProperty,
  releaseProperty,
} from "@/services/productServices/productActions";
import NotificationAlertOptions from "@/layouts/Alerts";
import AddPropertiesView from "./AddPropertiesView";

const initialValidation = {
  property: {
    name: true,
    label: true,
    description: true,
    type: true,
    order_listing: true,
    order_product: true,
  },
};

export default function AddPropertiesContainer() {
  const dispatch = useAppDispatch();

  const permission = useCurrentPermission();
  const currentWebsite = useAppSelector((s) => s.sites?.site || {});

  const canEdit = !!(permission?.edit || permission?.publish);


  const [property, setProperty] = useState(() => ({
    name: "",
    label: [],           
    description: "",
    type: "",
    order_listing: "",
    order_product: "",
    show: [{ key: currentWebsite?._id, value: false }],
    box: [{ key: currentWebsite?._id, value: false }],
  }));
  const [validation, setValidation] = useState(initialValidation);
  // SCRUM-1819: the soft-deleted property a 409 named, plus the payload that
  // got the 409 — Start fresh re-sends exactly that.
  const [deletedHolder, setDeletedHolder] = useState<{ id: string, msg: string, value: string, payload: any } | null>(
    null
  );

  // when site changes, ensure show/box seed uses new site key
  useEffect(() => {
    setProperty((p) => ({
      ...p,
      show: [{ key: currentWebsite?._id, value: p.show?.[0]?.value ?? false }],
      box: [{ key: currentWebsite?._id, value: p.box?.[0]?.value ?? false }],
    }));
  }, [currentWebsite?._id]);


  const validateField = useCallback((fieldName, value) => {
    setValidation((prev) => {
      const next = { ...prev, property: { ...prev.property } };
      switch (fieldName) {
        case "name":
          next.property.name = value.length > 0;
          break;
       
        case "description":
          next.property.description = value.length > 0;
          break;
        case "type":
          next.property.type = value.length > 0;
          break;
        case "order_listing":
          next.property.order_listing = String(value).length > 0;
          break;
        case "order_product":
          next.property.order_product = String(value).length > 0;
          break;
        default:
          break;
      }
      return next;
    });
  }, []);


  const onChange = useCallback(
    (e) => {
      const { name, value } = e.target;
      validateField(name, value);
      setProperty((p) => ({ ...p, [name]: value }));
    },
    [validateField]
  );

  const onChangeCheckboxShow = useCallback(
    (e) => {
      const value = e.target.name === "show" ? e.target.checked : e.target.value;
      setProperty((p) => ({
        ...p,
        show: [{ key: currentWebsite?._id, value }],
      }));
    },
    [currentWebsite?._id]
  );

  const onChangeCheckboxBox = useCallback(
    (e) => {
      const value = e.target.name === "box" ? e.target.checked : e.target.value;
      setProperty((p) => ({
        ...p,
        box: [{ key: currentWebsite?._id, value }],
      }));
    },
    [currentWebsite?._id]
  );

  const resetForm = useCallback(() => {
    setProperty((p) => ({
      ...p,
      name: "",
      description: "",
      type: "",
      order_listing: "",
      order_product: "",
      show: [{ key: currentWebsite?._id, value: false }],
      box: [{ key: currentWebsite?._id, value: false }],
    }));
    setValidation(initialValidation);
  }, [currentWebsite?._id]);

  // SCRUM-883: reset the form on addProperty's OWN resolved value (its own
  // signal), not by reading the shared appMessage the hub clears — otherwise
  // the child-before-parent effect order races the hub's clear and the reset
  // (or the toast) is lost. The success/error toasts are raised by the global
  // notification hub (addProperty publishes them to appMessage / state.errors).
  // SCRUM-1819: the one failure this form acts on is the 409 whose body names
  // the deleted property that still holds the name.
  const submitProperty = useCallback(
    async (payload) => {
      setDeletedHolder(null);
      const created = await dispatch(addProperty(payload));
      if (!created) return;
      if ("deletedHolder" in created) {
        const first = created.deletedHolder;
        setDeletedHolder({
          id: String(first.deletedId),
          msg: String(first.msg ?? "A deleted property holds this name"),
          value: String(first.value ?? ""),
          payload,
        });
        return;
      }
      dispatch(clearMessage());
      resetForm();
    },
    [dispatch, resetForm]
  );

  const onClickAdd = useCallback(
    () => submitProperty({ property }),
    [submitProperty, property]
  );

  const onRestore = useCallback(async () => {
    if (!deletedHolder) return;
    try {
      await dispatch(restoreProperty(deletedHolder.id)).unwrap();
    } catch {
      return; // surfaced via the hub; the offer stays on screen
    }
    setDeletedHolder(null);
    resetForm();
    NotificationAlertOptions("success", "The deleted property is restored");
  }, [dispatch, deletedHolder, resetForm]);

  const onStartFresh = useCallback(async () => {
    if (!deletedHolder) return;
    try {
      await dispatch(releaseProperty(deletedHolder.id)).unwrap();
    } catch {
      return; // surfaced via the hub; the offer stays on screen
    }
    await submitProperty(deletedHolder.payload);
  }, [dispatch, deletedHolder, submitProperty]);

  // derive checked states
  const showChecked = useMemo(() => !!property.show?.[0]?.value, [property.show]);
  const boxChecked = useMemo(() => !!property.box?.[0]?.value, [property.box]);

  if (!canEdit) return null;

  return (
    <AddPropertiesView
      property={property}
      validation={validation}
      showChecked={showChecked}
      boxChecked={boxChecked}
      onChange={onChange}
      onChangeCheckboxShow={onChangeCheckboxShow}
      onChangeCheckboxBox={onChangeCheckboxBox}
      onClickAdd={onClickAdd}
      deletedHolder={deletedHolder}
      onRestore={onRestore}
      onStartFresh={onStartFresh}
    />
  );
}
